import {asyncLoader} from '../async-loader/winelist-async-loader'
import applogger from '@applogger'
import createProfiler from 'app/profiler'
import CataloguesStore from 'dev/multicatalogue-editor/store'

const sortProductCatalog = (catalog, sortOrder) => {
  if (Object.entries(sortOrder).length !== 0) {
    return catalog.sort((a, b) => {
      return sortOrder.indexOf(a['id']) - sortOrder.indexOf(b['id'])
    })
  }
  else {
    return catalog.sort((a, b) => {
      if (Object.prototype.hasOwnProperty.call(a, 'sticky') && Object.prototype.hasOwnProperty.call(b, 'sticky')) {
        if (a.sticky > b.sticky) return 1
        if (a.sticky < b.sticky) return -1
      }
      if (Object.prototype.hasOwnProperty.call(a, 'order') && Object.prototype.hasOwnProperty.call(b, 'order')) {
        if (a.order > b.order) return 1
        if (a.order < b.order) return -1
      }
      return 0
    })
  }
}

export default async function(store, profiler = createProfiler(false)) {
  // 'catalogues' is registered by multicatalogue-editor's admin plugin
  // (SERVER_BUILD !== 'ssr'-gated - it's an admin-only editor UI), but the
  // module itself holds real published-catalogue data that a completely
  // separate, publicly-rendered component (RouteCatalog.vue, in
  // vuejs-ecommerce-product-catalog) reads directly via
  // catalogues/isActive and catalogues/getSingleCatalogue - which means
  // every SSR render of a catalogue page was throwing
  // "getters['catalogues/isActive'] is not a function" and falling back to
  // the SPA shell, 100% of the time. This function runs inside
  // product-catalogue-page.vue's asyncData, which resolves before
  // RouteCatalog (its child) renders, so registering it here - guarded,
  // since the admin plugin may also register it client-side - guarantees
  // it exists in time regardless of admin/SSR status.
  if (!store.hasModule('catalogues')) {
    store.registerModule('catalogues', CataloguesStore)
  }

  let winecatalogRefs = store.getters['getAdHocStorageValue']('winecatalog')

  profiler.start('wine-catalog:loadCatalogues')
  await store.dispatch('catalogues/loadCatalogues')
  profiler.end('wine-catalog:loadCatalogues')

  if (winecatalogRefs !== undefined) {
    applogger.debug('Wine catalog loader already complete')
    // adhocStorage stores {id}-only references (see the commit below) to
    // avoid duplicating the full mapped product data already in
    // products/products - resolve back to full objects here so this
    // function's return value stays a real usable catalog.
    return Promise.resolve(winecatalogRefs.map(ref => store.getters['products/findProductByID'](ref.id) || ref))
  }

  // let products = store.getters['products/all']
  //
  // if (products.length > 0) {
  //   applogger.debug('Wine catalog loader already complete')
  //   return Promise.resolve(products)
  // }

  const __loader = () => {
    profiler.start('wine-catalog:asyncLoader')
    // Server-side this preload always fetches the full catalog, as before -
    // pruneUnreferencedProducts (store/index.js) trims it down after render.
    // Client-side, this preload only actually runs when adhocStorage.
    // winecatalog is missing (the `winecatalogRefs !== undefined` check
    // above), which pruning deliberately clears - i.e. this is the
    // "immediate lite fetch on hydration" tier, requesting the same trimmed
    // field set as the SSR path so the client's initial backfill stays
    // cheap; the existing 30s-delayed background refresh (ecommerce.js)
    // still requests the full catalog afterward.
    const forceLite = process.env.SERVER_BUILD !== 'ssr'
    return asyncLoader(store, '', profiler, forceLite)
      .then(r => {
        profiler.end('wine-catalog:asyncLoader')
        return r
      })
      .then(_ => {
        profiler.start('wine-catalog:findByType')
        return store.dispatch('products/findByType', 'wine_product')
      })
      .then(winecatalog => {
        profiler.end('wine-catalog:findByType')
        const sortOrder = store.getters['blob/get']('wine-product-sort-order')
        const sortedWineCatalog = sortProductCatalog(winecatalog, sortOrder)

        // {id}-only references, sorted - not full product objects, which are
        // already in products/products. Storing the full catalog here used
        // to double the SSR-inlined state size for the wine catalog
        // specifically (~1.44MB on a 261-product site). Consumers of
        // getAdHocStorageValue('winecatalog') dereference against
        // products/findProductByID at their own read site.
        store.commit('STORE_ADHOC_STORAGE', { key: 'winecatalog', data: sortedWineCatalog.map(p => ({ id: p.id })) })

        applogger.debug('Wine catalog loader complete')
        return sortedWineCatalog
      })
  }

  return store.dispatch('promisePool', {
    name: 'wine-product-catalog',
    resolve: __loader
  })
}
